import classNames from "classnames";
import {useEffect, useMemo, useRef} from "react";
import {FiLoader, FiStopCircle} from "react-icons/fi";

import {AssistantIcon} from "@/components/assistant/AssistantIcon.tsx";
import {CollapsiblePanel} from "@/components/collapsiblePanel/CollapsiblePanel.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {ANALYZER_POLL_EDIT_INTRO} from "@/pages/analyzer/Analyzer.constants.ts";
import {AnalyzerPhase, ChatItemType, SSEErrorEvent, SSEEventType} from "@/pages/analyzer/Analyzer.types.ts";
import {buildChatItems} from "@/pages/analyzer/Analyzer.utils.ts";
import {AnalyzerChatHeader} from "@/pages/analyzer/components/chat/AnalyzerChatHeader.tsx";
import {AnalyzerChatInput} from "@/pages/analyzer/components/chat/AnalyzerChatInput.tsx";
import {AnalyzerChatItem} from "@/pages/analyzer/components/chat/AnalyzerChatItem.tsx";
import {AnalyzerChatMessage} from "@/pages/analyzer/components/chat/AnalyzerChatMessage.tsx";
import {AnalyzerPollCard} from "@/pages/analyzer/components/chat/AnalyzerPollCard.tsx";
import {AnalyzerRetryAlert} from "@/pages/analyzer/components/chat/AnalyzerRetryAlert.tsx";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";

import styles from "./AnalyzerChatPanel.module.scss";

const FOLLOW_BOTTOM_THRESHOLD_PX = 24;

const CHAT_ITEM_SPACING_CLASSES: Record<ChatItemType, string | undefined> = {
  [ChatItemType.MESSAGE]: styles.message,
  [ChatItemType.USER_MESSAGE]: styles.userMessage,
  [ChatItemType.TOOL_GROUP]: styles.toolGroup,
  [ChatItemType.POLL]: styles.poll,
  [ChatItemType.ANSWER_CHIP]: styles.answerChip,
  [ChatItemType.NOTICE]: undefined,
  [ChatItemType.SETTINGS]: styles.settings,
  [ChatItemType.ERROR]: undefined,
};

export const AnalyzerChatPanel = () => {
  const {events, phase, error, pollEdit, pendingRevisionRequest} = useAnalyzerContext();
  const {user} = useCurrentUserContext();
  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => {
    const built = buildChatItems(events, !!user?.has_write_access);
    if (pendingRevisionRequest) {
      built.push({type: ChatItemType.USER_MESSAGE, content: pendingRevisionRequest});
    }
    return built;
  }, [events, user, pendingRevisionRequest]);
  const lastItem = items[items.length - 1];
  const isToolGroupRunning = lastItem?.type === ChatItemType.TOOL_GROUP && !lastItem.isComplete;
  const isThinking = phase === AnalyzerPhase.STREAMING && !isToolGroupRunning;
  const lastErrorEvent = useMemo(
    () => [...events].reverse().find((event): event is SSEErrorEvent => event.type === SSEEventType.ERROR),
    [events]
  );
  // Stream-level failures (429, connection lost) set `error` without an ERROR event in the list
  const streamError = error !== null && error !== lastErrorEvent?.message ? error : null;

  useEffect(() => {
    const list = listRef.current;
    const content = contentRef.current;
    if (!list || !content) {
      return;
    }
    let followBottom = true;
    const trackScroll = () => {
      followBottom = list.scrollHeight - list.scrollTop - list.clientHeight <= FOLLOW_BOTTOM_THRESHOLD_PX;
    };
    const observer = new ResizeObserver(() => {
      if (followBottom) {
        list.scrollTo({top: list.scrollHeight});
      }
    });
    list.addEventListener("scroll", trackScroll);
    observer.observe(content);
    return () => {
      list.removeEventListener("scroll", trackScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <CollapsiblePanel pinned={<AssistantIcon size="medium" />} expandLabel="Expand chat">
      <AnalyzerChatHeader />
      <div className={styles.list} ref={listRef} role="log" aria-live="polite">
        <div ref={contentRef}>
          {items.map((item, index) => (
            <AnalyzerChatItem
              key={index}
              item={item}
              isLast={index === items.length - 1}
              className={classNames(styles.item, CHAT_ITEM_SPACING_CLASSES[item.type])}
            />
          ))}
          {pollEdit && (
            <>
              <AnalyzerChatMessage
                content={ANALYZER_POLL_EDIT_INTRO}
                className={classNames(styles.item, styles.message)}
              />
              <AnalyzerPollCard event={pollEdit} mode="edit" className={classNames(styles.item, styles.poll)} />
            </>
          )}
          {isThinking && (
            <div className={styles.thinking}>
              <Icon icon={FiLoader} className="pulseSpin" size="tiny" removeDivDimensions />
              Thinking…
            </div>
          )}
          {streamError && (
            <AnalyzerRetryAlert callout="Analysis failed" className={styles.item}>
              {streamError}
            </AnalyzerRetryAlert>
          )}
          {phase === AnalyzerPhase.ABORTED && !error && (
            <AnalyzerRetryAlert
              callout="Analysis stopped"
              variant="default"
              icon={FiStopCircle}
              className={styles.item}
            >
              You can retry to continue, or use Start Over to begin again.
            </AnalyzerRetryAlert>
          )}
        </div>
      </div>
      <AnalyzerChatInput />
    </CollapsiblePanel>
  );
};
